/* eslint-disable max-lines */
import { useQuery } from "@tanstack/react-query";
import {
  getSnowflakeJobGraphData,
  getSnowflakeJobGraphSummary,
  getWorkloadAttributes,
} from "@/helpers/apis";
import { Chart, ChartTypes } from "@uicore";
import { Stack } from "@altimateai/lego";
import { TotalCostHeader } from "@/pages/query/common/TotalCostHeader";
import { getWorkflowTerminology } from "@/pages/query/snowflake";
import GroupByDropdown from "@/pages/query/components/GroupByDropdown";
import { NativeSelect } from "@altimateai/lego";
import {
  appsFilter as appsFilterConfig,
  teamsFilter as teamsFilterConfig,
} from "@/pages/query/utils/snowflakeJobsFilterConfig";
import { FacetCombobox } from "./FacetCombobox";
import {
  AGGREGATE_BY_OPTIONS,
  createColorMapFromObjectKeys,
  getPercentKey,
  OTHER,
} from "@/pages/query/common";
import dayjs from "dayjs";
import { formatCost } from "@/helpers/utils";
import { Bar, Rectangle, TooltipProps } from "recharts";
import { CostChartTooltip } from "@/pages/query/Graph";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import {
  getDateByEndOfDayWithoutTimeoffset,
  getDateByStartOfDayWithoutTimeoffset,
} from "@/components/dateRange/utils";
import {
  GetSnowflakeJobGraphDataRequestParams,
  SnowflakeJobFilters,
  WorkflowType,
} from "@/pages/query/types";
import { useCustomTagFilters } from "@/pages/query/hooks/useCustomTagFilters";
import { useDatatableContext } from "@/components/tables/contexts/DatatableContextProvider";
import { useAppState } from "@/modules/app/useAppContext";
import { useDateFromSearchParams } from "@/helpers/useTimeHooks";
import { Spinner } from "@altimateai/lego";
import { EntityLegendPicker } from "./EntityLegendPicker";

interface CustomWorkloadsGraphProps {
  filters?: SnowflakeJobFilters;
  workflowType: WorkflowType;
}

/**
 * Formats a date value for the graph x-axis based on the aggregation interval.
 * Exported for testability.
 */
export const formatGraphDate = (
  value: string,
  aggregateBy: "day" | "week" | "month" | "year"
): string => {
  if (aggregateBy === "year") {
    return dayjs(value).format("YYYY");
  }
  if (aggregateBy === "month") {
    return dayjs(value).format("MMM YYYY");
  }
  if (aggregateBy === "week") {
    return dayjs(value).format("MMM DD");
  }
  return dayjs(value).format("MMM-DD");
};

export const CustomWorkloadsGraph: FC<CustomWorkloadsGraphProps> = ({ filters, workflowType }) => {
  const { getFilterValue, isInitialized } = useDatatableContext();
  const { currency } = useAppState();
  const [groupBy, setGroupBy] = useState<string>("project_id");
  const [aggregateBy, setAggregateBy] = useState<"day" | "week" | "month" | "year">("day");
  const [selectedLegends, setSelectedLegends] = useState<string[]>([]);
  const [selectedBuiltInFilters, setSelectedBuiltInFilters] = useState<string[]>([]);
  const { startDate, endDate } = useDateFromSearchParams();

  const teamsFilter = useMemo(() => {
    return getFilterValue<string[]>("teams") || [];
  }, [getFilterValue]);

  const appsFilter = useMemo(() => {
    return getFilterValue<string[]>("apps") || [];
  }, [getFilterValue]);

  const workflowFilter = useMemo(() => {
    return getFilterValue<string[]>("workflows") || [];
  }, [getFilterValue]);

  const warehouseFilter = useMemo(() => {
    return getFilterValue<string[]>("warehouses") || [];
  }, [getFilterValue]);

  const instanceFilter = useMemo(() => {
    return getFilterValue<string[]>("instance_id") || [];
  }, [getFilterValue]);

  const customTagFilters = useCustomTagFilters(filters?.custom_tag_keys, getFilterValue);

  const isProjectSelected = groupBy === "project_id";
  const isAppSelected = groupBy === "app_id";

  const graphTeamsFilter = useMemo(() => {
    if (isProjectSelected && selectedBuiltInFilters.length > 0) {
      return selectedBuiltInFilters;
    }
    return teamsFilter;
  }, [isProjectSelected, selectedBuiltInFilters, teamsFilter]);

  const graphAppsFilter = useMemo(() => {
    if (isAppSelected && selectedBuiltInFilters.length > 0) {
      return selectedBuiltInFilters;
    }
    return appsFilter;
  }, [isAppSelected, selectedBuiltInFilters, appsFilter]);

  const graphParams: GetSnowflakeJobGraphDataRequestParams = {
    start_date: getDateByStartOfDayWithoutTimeoffset(startDate).toISOString(),
    end_date: getDateByEndOfDayWithoutTimeoffset(endDate)?.toISOString(),
    workflows: workflowFilter,
    warehouses: warehouseFilter,
    teams: graphTeamsFilter,
    apps: graphAppsFilter,
    aggregate_by: groupBy,
    workflow_type: workflowType,
    aggregate_date_interval: aggregateBy,
    instance_id: instanceFilter.length > 0 ? instanceFilter : undefined,
    custom_tag_filters: Object.keys(customTagFilters).length > 0 ? customTagFilters : undefined,
  };

  const { data: groupByOptionsData } = useQuery({
    queryKey: ["workload", "attributes"],
    queryFn: getWorkloadAttributes,
  });

  // Custom tags don't apply to TABLEAU — strip them from the GroupByDropdown
  // so users can't pick a custom-tag groupBy that the bulk endpoint won't
  // populate. The custom-tag *filter* dropdowns are also implicitly disabled
  // because bulk.custom_tag_keys is [] for TABLEAU.
  const groupByOptions = useMemo(() => {
    if (!groupByOptionsData?.options) return undefined;
    if (workflowType === WorkflowType.TABLEAU) {
      return groupByOptionsData.options.filter(o => !o.is_custom);
    }
    return groupByOptionsData.options;
  }, [groupByOptionsData?.options, workflowType]);

  const graphCommonKeys = [
    "custom_workloads",
    groupBy,
    startDate,
    endDate,
    ...workflowFilter,
    ...warehouseFilter,
    ...graphTeamsFilter,
    ...graphAppsFilter,
    ...instanceFilter,
    workflowType,
    ...selectedBuiltInFilters,
    aggregateBy,
    customTagFilters,
  ];

  const graphDataQueryKey = ["graph_data", ...graphCommonKeys];
  const graphSummaryQueryKey = ["graph_summary", ...graphCommonKeys];

  const { data: graphData, isLoading: isGraphLoading } = useQuery({
    queryKey: graphDataQueryKey,
    queryFn: () => getSnowflakeJobGraphData(graphParams),
    enabled: isInitialized,
  });

  const isCustomTagSelected = useMemo(() => {
    if (!groupByOptionsData?.options) return false;
    const option = groupByOptionsData.options.find(opt => opt.key === groupBy);
    return option?.is_custom ?? false;
  }, [groupByOptionsData?.options, groupBy]);

  // Top entities by total cost across the visible date range.
  //
  // Both default selection and the "are there enough entities to need an OTHER
  // bucket?" decision derive from this single list, uniform across all groupBy
  // types. This keeps the chart's initial framing consistent: the bars users
  // see by default are always the highest-cost entities, never an arbitrary
  // slice driven by entity-list ordering from a different API.
  const entitiesByCost = useMemo(() => {
    if (!graphData) return [];
    const totalCost: Record<string, number> = {};
    for (const item of graphData) {
      for (const key in item) {
        if (key === "date") continue;
        const nested = item[key];
        const id = nested?.dbt_project_name === null ? key : nested?.dbt_project_name;
        if (!id) continue;
        totalCost[id] = (totalCost[id] || 0) + (nested?.cost_value || 0);
      }
    }
    return Object.entries(totalCost)
      .sort((a, b) => b[1] - a[1])
      .map(x => x[0]);
  }, [graphData]);

  // Default selection: top-10 by cost, plus OTHER when there are more than 10
  // entities (so the long tail still has a visible bucket in the chart).
  const defaultSelectedLegends = useMemo(() => {
    return (entitiesByCost.length > 10 ? [OTHER] : []).concat(entitiesByCost.slice(0, 10));
  }, [entitiesByCost]);

  // Re-seed `selectedLegends` from defaults only when the data scope itself
  // changes (groupBy / workflow / date range) — gated by a ref so we don't
  // overwrite the user's explicit picks on every unrelated re-render. Within
  // a single scope, user selections from the picker are sticky.
  const lastResetKeyRef = useRef<string | null>(null);
  const resetKey = `${groupBy}|${workflowType}|${String(startDate)}|${String(endDate)}`;
  useEffect(() => {
    if (lastResetKeyRef.current === resetKey) return;
    if (defaultSelectedLegends.length === 0) return;
    lastResetKeyRef.current = resetKey;
    setSelectedLegends(defaultSelectedLegends);
  }, [resetKey, defaultSelectedLegends]);

  // For TABLEAU the bulk endpoint already includes teams (always 1) and apps
  // (≤13), so a static Combobox is plenty. For CUSTOM those facets are
  // unbounded — use the paginated /teams or /apps endpoint via FacetCombobox
  // below.
  const facetCtx = useMemo(
    () => ({ startDate, endDate, workflowType }),
    [startDate, endDate, workflowType]
  );
  const builtInFacet = isProjectSelected
    ? teamsFilterConfig(facetCtx, filters?.teams)
    : isAppSelected
      ? appsFilterConfig(facetCtx, filters?.apps)
      : null;

  // Clear the picker selection when groupBy swaps so we don't forward the
  // previous facet's values into the new one (e.g. team names submitted as
  // apps=<...> after switching project_id → app_id).
  useEffect(() => {
    setSelectedBuiltInFilters([]);
  }, [groupBy]);

  const graphDataSubset = useMemo(() => {
    if (!graphData) {
      return [];
    }

    const result = [];
    for (const item of graphData) {
      const resultItem: { date?: string } & Record<string, number> = {};
      resultItem["date"] = item.date;

      for (const key in item) {
        if (key === "date") {
          continue;
        }

        const nestedObject = item[key];
        const identifier =
          nestedObject?.dbt_project_name === null ? key : nestedObject?.dbt_project_name;

        if (!identifier) {
          continue;
        }

        if (selectedLegends.includes(identifier)) {
          resultItem[identifier] = nestedObject?.cost_value || 0;

          if (result.length > 0) {
            const lastItem = result[result.length - 1];
            if (lastItem[identifier] > 0) {
              resultItem[getPercentKey(identifier)] =
                (nestedObject?.cost_value - lastItem[identifier]) / lastItem[identifier];
            }
          }
        } else {
          resultItem[OTHER] = resultItem[OTHER] || 0;
          resultItem[OTHER] += nestedObject?.cost_value || 0;

          if (result.length > 0) {
            const lastItem = result[result.length - 1];
            if (lastItem[OTHER] > 0) {
              resultItem[getPercentKey(OTHER)] =
                (nestedObject?.cost_value - lastItem[OTHER]) / lastItem[OTHER];
            }
          }
        }
      }

      result.push(resultItem);
    }

    return result;
  }, [graphData, selectedLegends]);

  const colorsMap = useMemo(() => {
    return createColorMapFromObjectKeys(graphData || [], {
      reservedMappings: { [OTHER]: "#5B9FFF" },
      excludeKeys: ["date"],
    });
  }, [graphData]);

  return (
    <Stack direction="column" className="al-p-3 al-border al-rounded-md">
      <Stack align="center" justify={"between"}>
        <TotalCostHeader
          queryKey={graphSummaryQueryKey}
          queryFn={getSnowflakeJobGraphSummary}
          params={graphParams}
          title={`Total ${getWorkflowTerminology(workflowType, "Jobs Cost")}`}
        />
        <Stack align={"center"}>
          <GroupByDropdown value={groupBy} onChange={setGroupBy} options={groupByOptions} />

          <NativeSelect
            label={"Aggregate By:"}
            showPlaceholder
            variant={"outline"}
            value={aggregateBy}
            onChange={value => setAggregateBy(value as "day" | "week" | "month" | "year")}
            options={[
              { label: "Daily", value: "day" },
              { label: "Weekly", value: "week" },
              { label: "Monthly", value: "month" },
              { label: "Yearly", value: "year" },
            ]}
            buttonProps={{ "aria-label": "Aggregate By" }}
          />

          {isCustomTagSelected && entitiesByCost.length > 0 && (
            <EntityLegendPicker
              groupBy={groupBy}
              startDate={startDate}
              endDate={endDate}
              workflowType={workflowType}
              value={selectedLegends}
              onChange={setSelectedLegends}
            />
          )}

          {builtInFacet && (
            <FacetCombobox
              facet={builtInFacet}
              value={selectedBuiltInFilters}
              onChange={setSelectedBuiltInFilters}
              placeholder={`Filter: All ${isProjectSelected ? "Projects" : "Apps"}`}
              header={isProjectSelected ? "Filter Projects" : "Filter Apps"}
              searchPlaceholder={`Search ${isProjectSelected ? "projects" : "apps"}...`}
            />
          )}
        </Stack>
      </Stack>
      <div className="al-border al-rounded-md al-p-4 al-pb-10">
        {isGraphLoading ? (
          <Stack className={"al-h-40"} justify={"center"} align={"center"}>
            <Spinner /> Loading graph...
          </Stack>
        ) : graphDataSubset.length > 0 ? (
          <Chart
            title="Cost"
            xAxisDataKey="date"
            yAxisLabel={{ value: "Cost" }}
            xAxisLabelFormatter={value => formatGraphDate(value, aggregateBy)}
            yAxisLabelFormatter={value => formatCost(value, currency)}
            type={ChartTypes.BarChart}
            data={graphDataSubset}
            hideFullscreenButton
            tooltipProps={{
              content: (props: TooltipProps<number, string>) => (
                <CostChartTooltip
                  {...props}
                  aggregateBy={AGGREGATE_BY_OPTIONS.find(i => i.value === groupBy)?.label!}
                />
              ),
            }}
            chartProps={{}}
          >
            {({ barSize, selectedLegend }) => {
              return (
                <>
                  {selectedLegends?.map((d, i) => (
                    <Bar
                      key={d}
                      barSize={Math.min(barSize, 60)}
                      maxBarSize={60}
                      dataKey={d}
                      fill={colorsMap[d]}
                      stackId="single"
                      hide={Boolean(selectedLegend && selectedLegend !== d)}
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      shape={(props: any) => {
                        props.radius =
                          i === selectedLegends.length - 1 ? [5, 5, 0, 0] : [0, 0, 0, 0];
                        return <Rectangle {...props} />;
                      }}
                    />
                  ))}
                </>
              );
            }}
          </Chart>
        ) : (
          <Stack className={"al-h-40"} justify={"center"} align={"center"}>
            No data available.
          </Stack>
        )}
      </div>
    </Stack>
  );
};
